import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { waitlistService } from '../services/waitlistService'
import toast from 'react-hot-toast'
import { 
  CameraIcon,
  ClockIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  CheckIcon,
  StarIcon,
  BoltIcon,
  ArrowRightIcon,
  SparklesIcon,
  CalendarIcon,
  EnvelopeIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWandSparkles } from '@fortawesome/free-solid-svg-icons'

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const CountdownPage = () => {
  const { user, isAdmin } = useAuth()
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const [launchDate, setLaunchDate] = useState<string>('2025-07-03T12:00:00Z')
  const [waitlistCount, setWaitlistCount] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    name: ''
  })

  // Function to inflate waitlist count for marketing purposes
  const getDisplayCount = (actualCount: number): number => {
    // Add a base number (e.g., 2000) plus a multiplier for social proof
    const baseInflation = 2000
    const multiplier = 1.5
    return Math.floor(actualCount * multiplier) + baseInflation
  }

  // Fetch launch settings and waitlist count
  useEffect(() => {
    const fetchData = async () => {
      const settings = await waitlistService.getLaunchSettings()
      if (settings) {
        setLaunchDate(settings.launch_date)
      }
      
      const count = await waitlistService.getWaitlistCount()
      setWaitlistCount(count)
    }
    
    fetchData()
  }, [])

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime()
      const launch = new Date(launchDate).getTime()
      const difference = launch - now

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        })
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [launchDate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.email.trim()) return

    setIsSubmitting(true)
    
    try {
      const result = await waitlistService.joinWaitlist(formData.email, formData.name)
      
      if (result.success) {
        setEmailSubmitted(true)
        toast.success('🎉 You\'re on the waitlist! We\'ll notify you when we launch.')
        // Update count
        const newCount = await waitlistService.getWaitlistCount()
        setWaitlistCount(newCount)
      } else {
        toast.error(result.error || 'Failed to join waitlist')
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const features = [
    {
      icon: CameraIcon,
      title: 'Smart Shoot Management',
      description: 'AI-powered scheduling and project organization that adapts to your workflow.',
      color: 'bg-brand-500/20 text-brand-400 dark:bg-brand-500/10 dark:text-brand-300',
      glowColor: 'group-hover:shadow-brand-500/20'
    },
    {
      icon: UserGroupIcon,
      title: 'Client Portal Revolution',
      description: 'Seamless client collaboration with real-time feedback and secure file sharing.',
      color: 'bg-brand-700/20 text-brand-600 dark:bg-brand-700/10 dark:text-brand-500',
      glowColor: 'group-hover:shadow-brand-700/20'
    },
    {
      icon: ClipboardDocumentListIcon,
      title: 'Intelligent Task Automation',
      description: 'Never miss a deadline with AI-driven task prioritization and reminders.',
      color: 'bg-emerald-500/20 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
      glowColor: 'group-hover:shadow-emerald-500/20'
    },
    {
      icon: DocumentTextIcon,
      title: 'Professional Invoicing',
      description: 'Automated invoicing with payment tracking and professional templates.',
      color: 'bg-orange-500/20 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300',
      glowColor: 'group-hover:shadow-orange-500/20'
    }
  ]

  const benefits = [
    { text: 'Save 15+ hours per week on admin tasks', icon: BoltIcon },
    { text: 'Increase client satisfaction by 40%', icon: StarIcon },
    { text: 'Never miss a deadline or payment', icon: CheckIcon },
    { text: 'Scale your freelance business efficiently', icon: ArrowRightIcon }
  ]

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            <div className="flex items-center space-x-3">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center space-x-2 lg:space-x-3"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 glass-badge rounded-2xl flex items-center justify-center">
                  <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <span className="text-xl lg:text-2xl font-bold text-white">
                  Shoot<span className="text-gray-400">Suite</span>
                </span>
              </motion.div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Admin bypass button */}
              {isAdmin() && (
                <Link 
                  to="/dashboard?admin=true"
                  className="btn-secondary text-sm"
                >
                  Admin Access
                </Link>
              )}
              
              {/* Sign in for users */}
              {!user && (
                <Link 
                  to="/auth"
                  className="text-gray-400 hover:text-white transition-colors font-medium"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="section-padding">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Coming Soon Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="inline-flex items-center space-x-2 px-6 py-3 mb-8 glass-badge rounded-full"
            >
              <SparklesIcon className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400 font-semibold">Coming Soon</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="heading-xl mb-6 text-white"
            >
              The Future of
              <span className="text-gradient bg-gradient-to-r from-brand-400 to-brand-600 bg-clip-text text-transparent">
                {' '}Creative Productivity{' '}
              </span>
              is Almost Here
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="body-lg text-gray-300 mb-12 max-w-3xl mx-auto"
            >
              ShootSuite is revolutionizing how photographers and videographers manage their business.
              Join thousands of creatives waiting for the ultimate productivity suite.
            </motion.p>

            {/* Countdown Timer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-12"
            >
              <div className="flex justify-center items-center space-x-2 mb-4">
                <ClockIcon className="w-6 h-6 text-brand-400" />
                <span className="text-lg font-semibold text-brand-400">Launch Countdown</span>
              </div>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="card text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                    {timeLeft.days}
                  </div>
                  <div className="text-gray-400 text-sm">Days</div>
                </div>
                <div className="card text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                    {timeLeft.hours}
                  </div>
                  <div className="text-gray-400 text-sm">Hours</div>
                </div>
                <div className="card text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                    {timeLeft.minutes}
                  </div>
                  <div className="text-gray-400 text-sm">Minutes</div>
                </div>
                <div className="card text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                    {timeLeft.seconds}
                  </div>
                  <div className="text-gray-400 text-sm">Seconds</div>
                </div>
              </div>
            </motion.div>

            {/* Waitlist Signup */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="max-w-md mx-auto"
            >
              {!emailSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-3">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-field w-full"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Your name (optional)"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-field w-full"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Joining...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <EnvelopeIcon className="w-5 h-5" />
                        <span>Join the Waitlist</span>
                      </div>
                    )}
                  </button>
                </form>
              ) : (
                <div className="card text-center">
                  <CheckIcon className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">You're In!</h3>
                  <p className="text-gray-300">
                    We'll notify you as soon as ShootSuite launches. Get ready to transform your workflow!
                  </p>
                </div>
              )}

              {/* Waitlist count */}
              <div className="mt-6 text-center">
                <p className="text-gray-400">
                  <span className="text-brand-400 font-semibold">{getDisplayCount(waitlistCount).toLocaleString()}</span>
                  {' '}creatives already on the waitlist
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Preview */}
        <section className="section-padding bg-black/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="heading-lg mb-6 text-white">
                What's Coming in
                <span className="text-gradient bg-gradient-to-r from-brand-400 to-brand-600 bg-clip-text text-transparent">
                  {' '}ShootSuite
                </span>
              </h2>
              <p className="body-lg text-gray-300 max-w-3xl mx-auto">
                We're building the most comprehensive productivity suite for creative professionals.
                Here's what you can expect at launch.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const IconComponent = feature.icon
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`group card hover:${feature.glowColor}`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${feature.color}`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-300">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="section-padding bg-gradient-to-br from-blue-900/10 to-purple-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
              {/* Story Content */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-center lg:text-left"
              >
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center space-x-2 glass-badge rounded-full px-4 py-2 mb-6"
                >
                  <CameraIcon className="w-5 h-5 text-brand-400" />
                  <span className="text-white font-medium">Our Story</span>
                </motion.div>

                <h2 className="heading-lg mb-6 text-white">
                  Born from <span className="text-gradient bg-gradient-to-r from-brand-400 to-brand-600 bg-clip-text text-transparent">Real Struggle</span>
                </h2>
                
                <div className="space-y-6 body-lg text-gray-300">
                  <p>
                    When I started as a freelance photographer and videographer, I quickly realized the biggest challenge wasn't capturing the perfect shot—it was managing everything else.
                  </p>
                  
                  <p>
                    I found myself juggling <strong className="text-white">three separate software tools</strong> just to run my business: one for scheduling shoots, another for client management, and a third for invoicing. The constant switching between platforms was eating away at my creative time and causing me to miss important deadlines.
                  </p>
                  
                  <p>
                    That's when I knew there had to be a better way. I envisioned a single dashboard where I could handle everything—from initial client contact to final payment—without the chaos of multiple tools.
                  </p>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="glass-badge rounded-2xl p-6 my-8"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <LightBulbIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-2">Our Mission</h3>
                        <p className="text-gray-300">
                          To help newer freelancers avoid the workflow chaos I experienced, while streamlining operations for established professionals who want to scale efficiently.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
              
              {/* Visual Elements */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="mt-16 lg:mt-0"
              >
                <div className="relative">
                  {/* Before vs After Workflow */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="space-y-8"
                  >
                    {/* Before - Multiple Tools */}
                    <div className="glass-badge rounded-2xl p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                        <h3 className="text-white font-semibold">Before: The Chaos</h3>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-center">
                          <CalendarIcon className="w-6 h-6 text-red-400 mx-auto mb-2" />
                          <p className="text-white text-sm font-medium">Tool A</p>
                          <p className="text-gray-400 text-xs">Scheduling</p>
                        </div>
                        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-center">
                          <UserGroupIcon className="w-6 h-6 text-red-400 mx-auto mb-2" />
                          <p className="text-white text-sm font-medium">Tool B</p>
                          <p className="text-gray-400 text-xs">Clients</p>
                        </div>
                        <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-3 text-center">
                          <DocumentTextIcon className="w-6 h-6 text-red-400 mx-auto mb-2" />
                          <p className="text-white text-sm font-medium">Tool C</p>
                          <p className="text-gray-400 text-xs">Invoicing</p>
                        </div>
                      </div>
                      <div className="mt-4 text-center">
                        <p className="text-red-400 text-sm">Constant switching • Lost time • Missed opportunities</p>
                      </div>
                    </div>

                    {/* Arrow */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 }}
                      className="flex justify-center"
                    >
                      <div className="w-12 h-12 bg-gradient-to-r from-brand-500 to-brand-600 rounded-full flex items-center justify-center">
                        <ArrowRightIcon className="w-6 h-6 text-white transform rotate-90 lg:rotate-0" />
                      </div>
                    </motion.div>

                    {/* After - ShootSuite */}
                    <div className="glass-badge rounded-2xl p-6 border-emerald-500/30">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-3 h-3 bg-emerald-400 rounded-full"></div>
                        <h3 className="text-white font-semibold">After: ShootSuite</h3>
                      </div>
                      <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-6 text-center">
                        <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <CameraIcon className="w-8 h-8 text-white" />
                        </div>
                        <p className="text-white font-bold text-lg mb-2">One Unified Dashboard</p>
                        <div className="flex justify-center space-x-4 mb-4">
                          <CalendarIcon className="w-5 h-5 text-emerald-400" />
                          <UserGroupIcon className="w-5 h-5 text-emerald-400" />
                          <DocumentTextIcon className="w-5 h-5 text-emerald-400" />
                          <ClipboardDocumentListIcon className="w-5 h-5 text-emerald-400" />
                        </div>
                        <p className="text-gray-300 text-sm">Shoots • Clients • Invoices • Tasks • Analytics</p>
                      </div>
                      <div className="mt-4 text-center">
                        <p className="text-emerald-400 text-sm">Streamlined workflow • More creative time • Growing business</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="section-padding">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="heading-lg mb-6 text-white">
                Why Creatives
                <span className="text-gradient bg-gradient-to-r from-emerald-400 to-brand-500 bg-clip-text text-transparent">
                  {' '}Love ShootSuite
                </span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon
                return (
                  <motion.div
                    key={benefit.text}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-4 p-6 glass-badge rounded-2xl"
                  >
                    <div className="w-10 h-10 bg-brand-500/20 rounded-xl flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-brand-400" />
                    </div>
                    <span className="text-white font-medium">
                      {benefit.text}
                    </span>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="section-padding bg-gradient-to-r from-brand-900/20 to-brand-800/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <FontAwesomeIcon 
                icon={faWandSparkles} 
                className="text-4xl text-brand-400 mb-6" 
              />
              <h2 className="heading-lg mb-6 text-white">
                Ready to Transform Your Creative Business?
              </h2>
              <p className="body-lg text-gray-300 mb-8">
                Don't miss out on early access to the productivity suite that will revolutionize 
                how you work. Join the waitlist today and be first to know when we launch.
              </p>
              
              {!emailSubmitted && (
                <Link 
                  to="#waitlist" 
                  className="btn-primary inline-flex items-center space-x-2"
                  onClick={() => {
                    document.querySelector('input[type="email"]')?.scrollIntoView({ 
                      behavior: 'smooth' 
                    })
                  }}
                >
                  <CalendarIcon className="w-5 h-5" />
                  <span>Get Early Access</span>
                </Link>
              )}
            </motion.div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="relative z-10 glass-footer mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 glass-badge rounded-xl flex items-center justify-center">
                <CameraIcon className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-gray-400 text-sm">
                © 2024 ShootSuite. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Launching July 3rd, 2025
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default CountdownPage 